/* eslint-disable max-lines */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from '@utils/i18n/i18n';

import { useAnswersForQuestionId } from '@services/query.service';
import questionOptions from '@App/components/Map/data/question-options';
import museumData from '@App/components/Map/data/musea-municipalities.json';
import { getLocalizedMuseumName } from '@App/pages/Tablet/Question1/question1-form.utils';
import { municipalityOptions } from '@App/pages/Tablet/Question2/municipality-data';

import '@App/components/AnswerRanking/answer-ranking.css';

function getOptions(questionId, lang) {
  if (questionId === 'question1') {
    return museumData.features.map(feature => ({
      label: getLocalizedMuseumName(feature, lang),
      value: feature.properties?.id,
    }));
  }

  if (questionId === 'question2') return municipalityOptions;

  return questionOptions[questionId] || [];
}

function getRankClass(rank) {
  if (rank === 1) return 'answer-ranking__rank--gold';
  if (rank === 2) return 'answer-ranking__rank--silver';
  if (rank === 3) return 'answer-ranking__rank--bronze';

  return '';
}

function AnswerRanking({ questionId, selectedAnswer }) {
  const { lang } = useTranslation();
  const {
    data: answerCounts = {},
    isLoading: isAnswerCountsLoading,
  } = useAnswersForQuestionId(questionId);
  const scrollRef = useRef(null);
  const ownAnswerRef = useRef(null);
  const [isOwnAnswerVisible, setIsOwnAnswerVisible] = useState(true);
  const options = getOptions(questionId, lang);
  const optionByValue = new Map(options.map(option => [String(option.value), option]));
  const selectedValue = isAnswerCountsLoading ? null : selectedAnswer?.value;
  const ranking = Object.entries(answerCounts)
    .map(([value, count]) => {
      const option = optionByValue.get(String(value));

      return {
        count: Number(count),
        label: option?.label
          || (String(value) === String(selectedValue) ? selectedAnswer?.label : value),
        value,
      };
    })
    .filter(item => Number.isFinite(item.count) && item.count > 0);

  if (selectedValue && !ranking.some(item => String(item.value) === String(selectedValue))) {
    ranking.push({
      count: 1,
      label: selectedAnswer.label || selectedValue,
      value: selectedValue,
    });
  }

  ranking.sort((first, second) => (
    second.count - first.count || first.label.localeCompare(second.label, lang)
  ));

  const ownAnswer = ranking.find(item => String(item.value) === String(selectedValue));

  useEffect(() => {
    const scrollElement = scrollRef.current;
    const ownAnswerElement = ownAnswerRef.current;

    if (!scrollElement || !ownAnswerElement) return undefined;

    const observer = new IntersectionObserver(([entry]) => {
      setIsOwnAnswerVisible(entry.isIntersecting && entry.intersectionRatio >= 1);
    }, {
      root: scrollElement,
      threshold: [1],
    });

    observer.observe(ownAnswerElement);

    return () => observer.disconnect();
  }, [ownAnswer]);

  function renderAnswerRow(item, rank, ref = null, isSticky = false) {
    const isOwnAnswer = String(item.value) === String(selectedValue);
    const rankClass = getRankClass(rank);
    const className = [
      'answer-ranking__item',
      isOwnAnswer ? 'answer-ranking__item--own' : '',
      isSticky ? 'answer-ranking__item--sticky' : '',
    ].filter(Boolean).join(' ');

    return (
      <div className={className} ref={ref}>
        <span className={`answer-ranking__rank ${rankClass}`}>{rank}</span>
        <span className="answer-ranking__label">{item.label}</span>
        <span className="answer-ranking__count">{item.count}</span>
      </div>
    );
  }

  return (
    <section className="answer-ranking" aria-label="Antwoorden gerangschikt op aantal">
      <div className="answer-ranking__scroll" ref={scrollRef}>
        <div className="answer-ranking__list">
          {ranking.map((item, index) => renderAnswerRow(
            item,
            index + 1,
            String(item.value) === String(selectedValue) ? ownAnswerRef : null,
          ))}
        </div>
        {ownAnswer && !isOwnAnswerVisible && renderAnswerRow(
          ownAnswer,
          ranking.indexOf(ownAnswer) + 1,
          null,
          true,
        )}
      </div>
    </section>
  );
}

export default AnswerRanking;
